import { useState, useCallback, useEffect, useRef } from 'react';
import { usePage } from '@inertiajs/react';
import SessionShell from '../shared/SessionShell';
import SessionCompleteSummary from '../shared/SessionCompleteSummary';
import McqDefinitionToWord from './McqDefinitionToWord';
import Cloze from './Cloze';
import SentenceWriting from './SentenceWriting';
import FlagQuestionButton from '../shared/FlagQuestionButton';
import TimedContinueButton from '../onboarding/TimedContinueButton';
import useLiveActivitySync from '../../../hooks/useLiveActivitySync';
import { Pause } from 'lucide-react';

/**
 * The vocabulary review-flow body. Mirrors OnboardingActivity's API so the
 * same component runs in three places:
 *  - Solo review page (`Student/Vocabulary/Review/Show.jsx`) — interactive
 *  - Live-session student lobby — interactive, broadcasts state via onStateChange
 *  - Live-session teacher show — `interactive={false}`, syncs from remoteState
 *
 * Per-question state (MCQ selected/result, cloze typed text + result) is
 * lifted up here so it can ride along on `progress_state` broadcasts and
 * reach the teacher's observer mirror without server round-trips. Keyed by
 * card_id so changing card resets it.
 */

// MCQ auto-advance window — currently inert. The setTimeout in
// handleMcqAnswer that would consume this has been commented out per
// user direction (UX-flow study window): every step now requires an
// explicit Continue press. Constant kept for the future-re-enable path.
// eslint-disable-next-line no-unused-vars
const MCQ_AUTO_ADVANCE_MS = 2000;

export default function ReviewActivity({
  session_id,
  total_items,
  status,
  current_index: initialIndex,
  current: initialCurrent,
  summary: initialSummary,
  onPause,
  onDone,
  interactive = true,
  remoteState,
  onStateChange,
  // Teacher's Step forward/back clicks arrive here as an incrementing signal
  // ({ direction, ts }). Mirrors OnboardingActivity so the teacher can drive
  // the student through the review queue from the observer view.
  stepSignal = null,
  // Bidirectional sync directives, same shape as OnboardingActivity.
  // Teacher's observer view emits peer_intent to drive student-side
  // affordances (open the cloze clue panel etc.); student receives via
  // peerIntent and applies locally, broadcasting the result back via
  // progress_state.
  peerIntent = null,
  onPeerIntent = null,
  // §3.15 — when set, this is the student's user_id and enables the
  // top-right Flag button for the teacher (observer mode). Posts to
  // /teacher/users/:user_id/vocabulary_flags. Mirrors the OnboardingActivity
  // prop of the same name.
  flagAsTeacherFor = null,
}) {
  const page = usePage();
  const [current, setCurrent] = useState(initialCurrent);
  const [index, setIndex] = useState(initialIndex || 0);
  const [complete, setComplete] = useState(status === 'completed');
  const [summary, setSummary] = useState(initialSummary || null);
  // Continue-gate state, mirroring OnboardingActivity. After the student
  // answers, we stash the backend's response in `lastResult` and reveal
  // the Continue button. For MCQ, an auto-advance timer also runs in
  // parallel (autoAdvanceAt drives the button's fill animation); for
  // Cloze + Sentence Writing, no auto-advance — the student reads the
  // feedback at their own pace.
  const [showContinue, setShowContinue] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [autoAdvanceAt, setAutoAdvanceAt] = useState(null);
  // Per-card transient state lifted out of the question components so it
  // can be broadcast.
  const [mcqSelected, setMcqSelected] = useState(null);
  const [mcqResult, setMcqResult] = useState(null);
  const [mcqCorrectChoiceId, setMcqCorrectChoiceId] = useState(null);
  const [clozeText, setClozeText] = useState('');
  const [clozeResult, setClozeResult] = useState(null);
  // Cloze §3.6 collapsible hint panel — lifted for live-session
  // bidirectional sync (same pattern as Stage 4's stage4HintOpen).
  const [clozeHintOpen, setClozeHintOpen] = useState(false);
  const autoAdvanceTimer = useRef(null);

  const csrfToken =
    typeof document !== 'undefined'
      ? document.querySelector('meta[name="csrf-token"]')?.content
      : '';

  useEffect(() => {
    return () => {
      if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    };
  }, []);

  // Reset per-card transient state when the active card changes (interactive
  // copy only — the observer mirror's state is dictated by remoteState).
  useEffect(() => {
    if (!interactive) return;
    setMcqSelected(null);
    setMcqResult(null);
    setMcqCorrectChoiceId(null);
    setClozeText('');
    setClozeResult(null);
    setClozeHintOpen(false);
    setShowContinue(false);
    setLastResult(null);
    setAutoAdvanceAt(null);
  }, [interactive, current?.card_id, current?.format]);

  // Re-seed local state if the parent re-fetches and hands us new initial
  // props (interactive: student returning to a paused session;
  // observer: teacher hits Refresh).
  useEffect(() => {
    if (interactive) return;
    if (initialCurrent && initialCurrent.card_id !== current?.card_id) {
      setCurrent(initialCurrent);
      setIndex(initialIndex || 0);
    }
  }, [initialCurrent?.card_id]);

  const postAnswer = useCallback(
    async (body) => {
      if (!interactive) return { complete: false, next: null };
      const res = await fetch(
        `/student/vocabulary/review_sessions/${session_id}/answer`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': csrfToken,
            Accept: 'application/json',
          },
          body: JSON.stringify(body),
        }
      );
      // Throw on 4xx/5xx so callers don't silently treat "undefined body"
      // as "answer not wrong" — which is how a transient render of a red X
      // over a correct answer can appear (see spec §3.20 follow-up).
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(`Answer POST failed: ${res.status} ${text.slice(0, 200)}`);
      }
      return res.json();
    },
    [session_id, csrfToken, interactive]
  );

  const advanceToNext = (data) => {
    setAutoAdvanceAt(null);
    if (data.complete) {
      setSummary(data.summary);
      setComplete(true);
    } else if (data.next) {
      setCurrent(data.next);
      setIndex((i) => i + 1);
    }
  };

  const handleContinue = () => {
    if (autoAdvanceTimer.current) {
      clearTimeout(autoAdvanceTimer.current);
      autoAdvanceTimer.current = null;
    }
    if (lastResult) advanceToNext(lastResult);
  };

  const handleMcqAnswer = async ({ choice_id, elapsed_ms }) => {
    const data = await postAnswer({ choice_id, elapsed_ms });
    setLastResult(data);
    setShowContinue(true);
    // Auto-advance disabled per user direction (UX-flow study window).
    // Student presses Continue to advance from MCQ feedback. The fill bar
    // on TimedContinueButton stays inert because autoAdvanceAt is null.
    // Scaffolding left behind a comment so we can re-enable with a
    // different cadence later if needed.
    //
    // if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    // setAutoAdvanceAt(Date.now());
    // autoAdvanceTimer.current = setTimeout(() => {
    //   autoAdvanceTimer.current = null;
    //   advanceToNext(data);
    // }, MCQ_AUTO_ADVANCE_MS);
    setAutoAdvanceAt(null);
    return data;
  };

  const handleClozeAnswer = async ({ text, elapsed_ms, attempt, hint_used }) => {
    const data = await postAnswer({ text, elapsed_ms, attempt, hint_used });
    if (data?.retry) return data;
    setLastResult(data);
    setShowContinue(true);
    // No auto-advance — Cloze has multi-line reveal copy (definition,
    // POS, "different form" / "spelling miss" feedback) that the
    // student needs time to read. They press Continue when ready.
    return data;
  };

  const handleSentenceWritingAnswer = async ({ text, elapsed_ms }) => {
    const data = await postAnswer({ text, elapsed_ms });
    setLastResult(data);
    setShowContinue(true);
    // No auto-advance — AI feedback needs reading time.
    return data;
  };

  // Live-session sync (peer_intent, remoteState pull, progress_state
  // broadcast, stepSignal, Enter-to-Continue, observer lock) lives in the
  // shared hook. This component injects only its own field set + the
  // broadcast deps. Review is forward-only: no onStepBack → back is a no-op.
  // NOTE: `broadcastState` and `broadcastDeps` must stay in lock-step — the
  // exhaustive-deps lint can't see a deps array passed as a variable.
  const { fieldsetProps } = useLiveActivitySync({
    interactive,
    remoteState,
    applyRemoteState: (rs) => {
      if (rs.current !== undefined) setCurrent(rs.current);
      if (rs.index !== undefined) setIndex(rs.index);
      if (rs.complete !== undefined) setComplete(rs.complete);
      if (rs.summary !== undefined) setSummary(rs.summary);
      if (rs.mcqSelected !== undefined) setMcqSelected(rs.mcqSelected);
      if (rs.mcqResult !== undefined) setMcqResult(rs.mcqResult);
      if (rs.mcqCorrectChoiceId !== undefined) setMcqCorrectChoiceId(rs.mcqCorrectChoiceId);
      if (rs.clozeText !== undefined) setClozeText(rs.clozeText);
      if (rs.clozeResult !== undefined) setClozeResult(rs.clozeResult);
      if (rs.clozeHintOpen !== undefined) setClozeHintOpen(rs.clozeHintOpen);
    },
    peerIntent,
    applyPeerIntent: (action, payload) => {
      if (action === 'set_cloze_hint') {
        if (payload?.open !== undefined) setClozeHintOpen(!!payload.open);
      }
    },
    onStateChange,
    broadcastState: {
      current,
      index,
      complete,
      summary,
      mcqSelected,
      mcqResult,
      mcqCorrectChoiceId,
      clozeText,
      clozeResult,
      clozeHintOpen,
    },
    broadcastDeps: [
      interactive,
      current?.card_id,
      current?.format,
      index,
      complete,
      summary,
      mcqSelected,
      mcqResult,
      mcqCorrectChoiceId,
      clozeText,
      clozeResult?.tier,
      clozeResult?.correctWord,
      clozeHintOpen,
    ],
    stepSignal,
    showContinue,
    onContinue: handleContinue,
    lastResult,
  });

  if (complete) {
    return (
      <SessionShell topLabel="Review complete">
        <SessionCompleteSummary
          wordsCount={summary?.total || total_items}
          firstAttemptRate={summary?.accuracy}
          tone={summary?.tone || 'neutral'}
          words={summary?.words || []}
          onDone={onDone}
          sessionType="review"
        />
      </SessionShell>
    );
  }

  if (!current) {
    return (
      <SessionShell topLabel={interactive ? 'Loading...' : 'Waiting for student to start…'}>
        <div className="text-center text-gray-500 dark:text-gray-400 py-12">
          {interactive ? 'Loading session...' : 'The student hasn\'t opened the first card yet.'}
        </div>
      </SessionShell>
    );
  }

  const remaining = total_items - index;
  const showPause = interactive && typeof onPause === 'function';

  // Single-section progress strip — matches onboarding's visual treatment
  // (one cell per card). The active cell is whichever card the student is
  // currently working on. Per-card grading happens before advanceToNext
  // fires, so `completed` stays at `index` while the student is reading
  // feedback; the active cell visually represents "the card I'm on now."
  const progressSections = [
    {
      label: 'Recall',
      units: total_items,
      completed: index,
      active: !complete,
    },
  ];

  return (
    <SessionShell
      topLabel={
        interactive
          ? `${remaining} ${remaining === 1 ? 'word' : 'words'} remaining`
          : `Watching · ${remaining} ${remaining === 1 ? 'word' : 'words'} remaining`
      }
      progressSections={progressSections}
      topRight={
        <div className="flex items-center gap-3">
          <FlagQuestionButton
            enabled={
              (interactive && Boolean(
                page.props?.auth?.impersonating?.active ||
                page.props?.auth?.user?.role === 'admin'
              )) ||
              (flagAsTeacherFor != null && page.props?.auth?.user?.role === 'teacher')
            }
            endpoint={
              flagAsTeacherFor != null && page.props?.auth?.user?.role === 'teacher'
                ? `/teacher/users/${flagAsTeacherFor}/vocabulary_flags`
                : '/student/vocabulary/flags'
            }
            payload={{
              user_word_card_id: current?.card_id,
              vocabulary_session_id: session_id,
              stage_or_format: current?.format,
            }}
          />
          {showPause && (
            <button
              onClick={onPause}
              className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1 transition-colors"
            >
              <Pause className="w-3.5 h-3.5" />
              Pause
            </button>
          )}
        </div>
      }
    >
      {/* Observer (teacher) lock. We deliberately do NOT use a disabled
          <fieldset> here: a disabled fieldset disables EVERY descendant with
          no way for a child to opt back in, which swallowed the teacher's
          cloze-clue toggle. pointer-events-none on the wrapper blocks the
          answer controls while letting designated peer-driven affordances
          (the clue toggle) re-enable themselves via .pointer-events-auto, so
          both peers can open/close the clue and it syncs via peer_intent. */}
      <fieldset {...fieldsetProps}>
      {current.format === 'mcq_def_to_word' && (
        <McqDefinitionToWord
          key={`mcq-${current.card_id}-${index}`}
          definition={current.payload.definition}
          pos={current.payload.pos}
          choices={current.payload.choices}
          onAnswer={handleMcqAnswer}
          selected={mcqSelected}
          result={mcqResult}
          correctChoiceId={mcqCorrectChoiceId}
          onSelect={setMcqSelected}
          onResult={setMcqResult}
          onCorrectChoiceId={setMcqCorrectChoiceId}
        />
      )}

      {current.format === 'cloze' && (
        <Cloze
          key={`cloze-${current.card_id}-${index}`}
          sentenceWithBlank={current.payload.sentence_with_blank}
          firstLetter={current.payload.first_letter}
          remainingLength={current.payload.remaining_length}
          maskPattern={current.payload.mask_pattern}
          acceptedSurfaceForm={current.payload.accepted_surface_form}
          definition={current.payload.definition}
          pos={current.payload.pos}
          sentenceHtml={current.payload.sentence_html}
          lemmaHintLength={current.payload.lemma_hint_length}
          onAnswer={handleClozeAnswer}
          text={clozeText}
          result={clozeResult}
          onTextChange={setClozeText}
          onResult={setClozeResult}
          hintOpen={clozeHintOpen}
          onHintOpenChange={(open) => {
            if (!interactive) {
              if (typeof onPeerIntent === 'function') {
                onPeerIntent('set_cloze_hint', { open });
              }
            } else {
              setClozeHintOpen(open);
            }
          }}
        />
      )}

      {current.format === 'sentence_writing' && (
        <SentenceWriting
          key={`sw-${current.card_id}-${index}`}
          lemma={current.payload.lemma}
          definition={current.payload.definition}
          prompt={current.payload.prompt}
          onAnswer={handleSentenceWritingAnswer}
          prereqFallback={lastResult?.prereq_fallback}
        />
      )}

      {/* Continue button — shown after the student answers; same component
          as onboarding so the look + Enter-key + timer-fill behaviors are
          identical. MCQ passes autoAdvanceAt to drive the 2s fill; Cloze
          and Sentence Writing pass null (manual advance only). */}
      {showContinue && interactive && (
        <div className="mt-8 flex justify-center">
          <TimedContinueButton
            autoAdvanceAt={autoAdvanceAt}
            duration={MCQ_AUTO_ADVANCE_MS}
            onClick={handleContinue}
          />
        </div>
      )}
      </fieldset>
    </SessionShell>
  );
}
